import { Injectable } from '@angular/core'
import { Uuid } from '@ftr/contracts/type/shared'
import { featureFlag } from '@ftr/ui-feature-flags'
import { UserState } from '@ftr/ui-user'
import { Store } from '@ngxs/store'
import { combineLatest, map, Observable, of, switchMap } from 'rxjs'
import { ExternalUserNavItemsProvider } from './external/external-user-nav-items.provider'
import { InternalUserNavItemsProvider } from './internal/internal-user-nav-items.provider'
import { PublicNavItemsProvider } from './public/public-nav-items.provider'
import { SideNavGroup, SideNavItem } from './side-nav-item'

@Injectable({
  providedIn: 'root',
})
export class SideNavItemsProvider {
  private readonly isPublicNavigationEnabled = featureFlag('public-navigation')

  constructor(
    // Provider for When a user is logged in and under a court system path that they have permissions for
    private readonly internalUserNavItemsProvider: InternalUserNavItemsProvider,
    // Provider for When a user is logged in and exited from all court system paths
    private readonly externalUserNavItemsProvider: ExternalUserNavItemsProvider,
    // Provider for When a user is not logged in and the public navigation feature flag is enabled
    private readonly publicNavItemsProvider: PublicNavItemsProvider,
    private readonly store: Store,
  ) {}

  provide(courtSystemId: Uuid | undefined): Observable<{ navGroups: SideNavGroup[]; footerItems: SideNavItem[] }> {
    const isLoggedIn$ = this.store.select(UserState.isLoggedIn)
    const hasPermissionsInCourtSystem$ = courtSystemId
      ? this.store.select(UserState.getEnabledPermissions).pipe(map(fn => fn(courtSystemId).length > 0))
      : of(false)

    return combineLatest([isLoggedIn$, hasPermissionsInCourtSystem$]).pipe(
      switchMap(([isLoggedIn, hasPermissions]) => {
        if (courtSystemId && hasPermissions) {
          return this.internalUserNavItemsProvider.provide(courtSystemId)
        } else if (this.isPublicNavigationEnabled() && !isLoggedIn) {
          return this.publicNavItemsProvider.provide()
        }
        return this.externalUserNavItemsProvider.provide()
      }),
    )
  }
}
